/**
 * Hook do Centro de Preferências de Notificação (tela dedicada).
 *
 * Lê o snapshot completo (canais, eventos, preferências módulo×canal e DND)
 * via RPC `get_my_notification_settings` e expõe mutações para salvar
 * preferência por célula e janela DND.
 *
 * @version 2.1.0 - 2026-07-02 — Guarda de tenant no `enabled` do useQuery: a RPC
 *                              `get_my_notification_settings` chama internamente
 *                              `get_validated_tenant_id()` e lança 400 quando o
 *                              usuário está autenticado mas sem tenant ativo
 *                              (fluxos: /onboarding, afiliado puro, painel WL,
 *                              convite pendente). Removido também o
 *                              `showToast.error` de dentro do `queryFn` para
 *                              evitar spam de toasts em qualquer erro transitório
 *                              — o consumidor da tela dedicada trata o `error`.
 * @version 2.0.0 - 2026-07-02 — Migrado para TanStack Query. Cache singleton por
 *                              usuário: múltiplos consumidores compartilham o
 *                              mesmo dado sem refetch. staleTime infinito porque
 *                              o snapshot só muda via mutação local.
 * @version 1.0.0 - 2026-06-05 — F3 concluída (tela /tenant/settings/notifications)
 */
import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { showToast } from '@/lib/toast';

const logger = createLogger('useNotificationSettings');

export interface NotificationChannel {
  code: string;
  name: string;
  is_external: boolean;
  display_order: number;
  is_active: boolean;
}

export interface NotificationEvent {
  code: string;
  module: string;
  name: string;
  default_priority: string;
}

export interface ChannelPreference {
  module: string;
  channel_code: string;
  is_enabled: boolean;
}

export interface DndSchedule {
  is_enabled: boolean;
  start_time: string;       // 'HH:MM'
  end_time: string;         // 'HH:MM'
  days_of_week: number[];   // ISO 1..7 (1=segunda)
  timezone: string;
  channels_silenced: string[];
}

export interface NotificationSettingsSnapshot {
  channels: NotificationChannel[];
  events: NotificationEvent[];
  modules: string[];
  preferences: ChannelPreference[];
  dnd: DndSchedule | null;
}

/** Canais considerados "implementados" hoje. Os demais aparecem como "Em breve". */
const IMPLEMENTED_CHANNELS = new Set(['in_app', 'sound', 'browser', 'push_fcm']);

/** Default: in_app/sound/browser/push_fcm habilitados; externos placeholder desativados. */
function isEnabledByDefault(channelCode: string): boolean {
  return IMPLEMENTED_CHANNELS.has(channelCode);
}

/**
 * Chave de cache compartilhada por todos os consumidores do snapshot de
 * preferências de notificação. Escopada por usuário para não vazar entre
 * sessões após logout/login.
 */
export const notificationSettingsQueryKey = (userId: string | undefined) =>
  ['notification-settings', userId ?? 'anon'] as const;

export function useNotificationSettings() {
  const { user, currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = notificationSettingsQueryKey(user?.id);

  // Fonte única do snapshot — todos os consumidores compartilham o mesmo cache.
  // staleTime Infinity porque o snapshot só muda por ação local (mutação abaixo)
  // ou logout — nunca por evento assíncrono.
  const {
    data,
    isLoading: loading,
    refetch,
  } = useQuery<NotificationSettingsSnapshot | null>({
    queryKey,
    // v2.1.0: só executa quando há tenant ativo. Sem essa guarda, o hook dispara
    // durante /onboarding, sessões de afiliado, painel WL e convites pendentes —
    // contextos em que a RPC responde 400 (get_validated_tenant_id RAISE).
    enabled: !!user?.id && !!currentTenant?.id,
    staleTime: Infinity,
    gcTime: 30 * 60_000, // 30 min em memória mesmo sem consumidores
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    queryFn: async () => {
      const { data: res, error } = await supabase.rpc('get_my_notification_settings');
      if (error) {
        // v2.1.0: apenas loga. Toast é responsabilidade da tela dedicada
        // (evita 3+ toasts empilhados em qualquer erro transitório).
        logger.error('Erro ao carregar preferências de notificação:', error);
        throw error;
      }
      return (res as unknown) as NotificationSettingsSnapshot;
    },
  });

  /**
   * Mapa rápido [module][channel] -> is_enabled, com fallback para o default
   * do canal quando não existe linha persistida.
   */
  const matrix = useMemo(() => {
    const map = new Map<string, boolean>();
    if (!data) return map;
    for (const m of data.modules) {
      for (const c of data.channels) {
        map.set(`${m}::${c.code}`, isEnabledByDefault(c.code));
      }
    }
    for (const p of data.preferences) {
      map.set(`${p.module}::${p.channel_code}`, p.is_enabled);
    }
    return map;
  }, [data]);

  // Mutação: atualiza uma célula da matriz de forma otimista.
  const setPreferenceMutation = useMutation({
    mutationFn: async (vars: { module: string; channelCode: string; isEnabled: boolean }) => {
      const { error } = await supabase.rpc('set_user_channel_preference', {
        p_module: vars.module,
        p_channel_code: vars.channelCode,
        p_is_enabled: vars.isEnabled,
      });
      if (error) throw error;
    },
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<NotificationSettingsSnapshot | null>(queryKey);
      queryClient.setQueryData<NotificationSettingsSnapshot | null>(queryKey, (prev) => {
        if (!prev) return prev;
        const without = prev.preferences.filter(
          (p) => !(p.module === vars.module && p.channel_code === vars.channelCode),
        );
        return {
          ...prev,
          preferences: [
            ...without,
            { module: vars.module, channel_code: vars.channelCode, is_enabled: vars.isEnabled },
          ],
        };
      });
      return { previous };
    },
    onError: (err, _vars, ctx) => {
      logger.error('Erro ao salvar preferência módulo×canal:', err);
      showToast.error('Não foi possível salvar a preferência.');
      if (ctx?.previous !== undefined) {
        queryClient.setQueryData(queryKey, ctx.previous);
      }
    },
  });

  const setPreference = useCallback(
    (module: string, channelCode: string, isEnabled: boolean) => {
      if (!data) return;
      return setPreferenceMutation.mutateAsync({ module, channelCode, isEnabled });
    },
    [data, setPreferenceMutation],
  );

  // Mutação: salva a janela DND completa.
  const setDndMutation = useMutation({
    mutationFn: async (next: DndSchedule) => {
      const { error } = await supabase.rpc('set_user_dnd_schedule', {
        p_is_enabled: next.is_enabled,
        p_start_time: next.start_time,
        p_end_time: next.end_time,
        p_days_of_week: next.days_of_week,
        p_timezone: next.timezone,
        p_channels_silenced: next.channels_silenced,
      });
      if (error) throw error;
    },
    onMutate: async (next) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<NotificationSettingsSnapshot | null>(queryKey);
      queryClient.setQueryData<NotificationSettingsSnapshot | null>(queryKey, (prev) =>
        prev ? { ...prev, dnd: next } : prev,
      );
      return { previous };
    },
    onError: (err, _next, ctx) => {
      logger.error('Erro ao salvar DND:', err);
      showToast.error('Não foi possível salvar o Não Perturbe.');
      if (ctx?.previous !== undefined) {
        queryClient.setQueryData(queryKey, ctx.previous);
      }
    },
  });

  const setDnd = useCallback(
    (next: DndSchedule) => setDndMutation.mutateAsync(next),
    [setDndMutation],
  );

  const saving = setPreferenceMutation.isPending || setDndMutation.isPending;

  return { data: data ?? null, loading, saving, matrix, setPreference, setDnd, refetch };
}
